// ============================================================================
// OBT F2-A — Kampanya tasarımcısı paket-yerel enum/etiket sabitleri
// ============================================================================
// KAYNAK (birebir backend'den doğrulandı):
//   - Soru tipleri:   backend/modules/obt/enums.py :: QuestionType
//   - Park özellik anahtarları: backend/modules/obt/scope.py :: PARK_FEATURE_REGISTRY
//   - filter_configuration şekli: scope.py :: validate_filter_configuration
//
// Bu dosya PAKET-YERELDİR. Ortak `obtEnums.js` DEĞİŞTİRİLMEZ (F2-B ile çakışma
// önlemi). Durum etiketleri gibi ortak/okunur değerler shared modülden import
// edilir; yalnız F2-A'ya özel tanımlar burada tutulur.
//
// Backend'de OLMAYAN hiçbir soru tipi, özellik anahtarı veya filtre alanı
// eklenmez. Koşullu (dallanan) soru backend'de DESTEKLENMEZ; bu yüzden burada
// da yoktur (talimat §7).
// ============================================================================

// `document` tipi backend enum'unda VAR ama belge yükleme HTTP ucu henüz YOK
// (talimat §9). Bu nedenle YENİ document sorusu eklenemez (disabled); mevcut bir
// taslakta bulunan document sorusu ise korunur, sessizce photo'ya çevrilmez.
export const OBT_DOCUMENT_UPLOAD_ENABLED = false;
export const OBT_DOCUMENT_DISABLED_NOTE =
  'Belge yükleme desteği henüz kullanıma açık değildir; bu tipte yeni soru eklenemez.';

/** enums.py :: QuestionType — birebir literal değerler. */
export const OBT_QUESTION_TYPES = Object.freeze([
  { value: 'boolean', label: 'Evet / Hayır', needsOptions: false, disabled: false },
  { value: 'text', label: 'Serbest Metin', needsOptions: false, disabled: false },
  { value: 'single_select', label: 'Tek Seçim', needsOptions: true, disabled: false },
  { value: 'multi_select', label: 'Çoklu Seçim', needsOptions: true, disabled: false },
  { value: 'photo', label: 'Fotoğraf', needsOptions: false, disabled: false },
  {
    value: 'document',
    label: 'Belge (yakında)',
    needsOptions: false,
    disabled: !OBT_DOCUMENT_UPLOAD_ENABLED,
  },
]);

/** Seçenek gerektiren tipler (service.py :: _SELECT_TYPES). */
export const OBT_SELECT_TYPES = Object.freeze(['single_select', 'multi_select']);

export function questionTypeNeedsOptions(type) {
  return OBT_SELECT_TYPES.includes(type);
}

/** `document` tipi (upload ucu gelene kadar yeni seçim için kapalı). */
export function isDocumentType(type) {
  return type === 'document';
}

/**
 * Backend `publication-preview` issue kodlarını (service.py :: _validate_content)
 * kullanıcıya gösterilecek Türkçe metne çevirir. Ham backend mesajı (soru ID'si
 * içerebilir) DOĞRUDAN gösterilmez; bilinmeyen kod için nötr bir metin döner.
 */
export const OBT_PREVIEW_ISSUE_LABELS = Object.freeze({
  deleted: 'Bilgi talebi silinmiş; yayınlanamaz.',
  not_draft: 'Bilgi talebi taslak durumunda değil; yeniden yayınlanamaz.',
  no_questions: 'En az bir soru eklenmelidir.',
  empty_question: 'Bir sorunun metni boş; lütfen doldurun.',
  duplicate_order: 'Soru sıralaması çakışıyor; sıralamayı düzeltin.',
  invalid_feature_key: 'Geçersiz park özelliği seçimi.',
  select_without_option: 'Seçimli bir soruda hiç seçenek yok.',
  scope: 'Seçilen hedeflerde yetki/kapsam sorunu var.',
});

export function previewIssueText(issue) {
  const code = issue?.code;
  if (code && OBT_PREVIEW_ISSUE_LABELS[code]) return OBT_PREVIEW_ISSUE_LABELS[code];
  return 'Yayın için giderilmesi gereken bir sorun var.';
}

export function questionTypeLabel(type) {
  const found = OBT_QUESTION_TYPES.find((t) => t.value === type);
  return found ? found.label : String(type || '');
}

/**
 * scope.py :: PARK_FEATURE_REGISTRY anahtarları (allowlist).
 * Etiketler yalnız kullanıcı gösterimi içindir; anahtarlar backend'e birebir
 * gider. Backend'de olmayan anahtar İCAT EDİLMEZ.
 */
export const OBT_PARK_FEATURES = Object.freeze([
  { key: 'basketball_court', label: 'Basketbol Sahası' },
  { key: 'football_field', label: 'Futbol Sahası' },
  { key: 'fountain', label: 'Havuz / Süs Havuzu' },
  { key: 'parking', label: 'Otopark' },
  { key: 'playground', label: 'Çocuk Oyun Grubu' },
  { key: 'running_track', label: 'Koşu / Yürüyüş Parkuru' },
  { key: 'sports_equipment', label: 'Spor Aletleri' },
  { key: 'tennis_court', label: 'Tenis Kortu' },
  { key: 'volleyball_court', label: 'Voleybol Sahası' },
  { key: 'wc', label: 'Tuvalet (WC)' },
]);

export const OBT_PARK_FEATURE_KEYS = Object.freeze(
  OBT_PARK_FEATURES.map((f) => f.key)
);

export function parkFeatureLabel(key) {
  const found = OBT_PARK_FEATURES.find((f) => f.key === key);
  return found ? found.label : String(key || '');
}

/** scope.py :: _ALLOWED_FILTER_MATCH (varsayılan "any"). */
export const OBT_FILTER_MATCH = Object.freeze([
  { value: 'any', label: 'Herhangi biri (seçilen özelliklerden en az biri)' },
  { value: 'all', label: 'Tümü (seçilen özelliklerin hepsi)' },
]);

/**
 * scope.py :: TARGETABLE_ROLES — kampanya hedefi olabilecek roller.
 * Hedef seçici bu rollere göre filtrelenir; backend yayın anında yeniden
 * doğrular (istemci filtresi yalnız UX'tir).
 */
export const OBT_TARGETABLE_ROLES = Object.freeze([
  'mudur_yardimcisi',
  'bolge_muhendisi',
  'formen',
]);

/** scope.py :: PUBLISHER_ROLES — kampanya yayınlayabilen roller. */
export const OBT_PUBLISHER_ROLES = Object.freeze([
  'mudur',
  'mudur_yardimcisi',
  'bolge_muhendisi',
]);

export const OBT_ROLE_LABELS = Object.freeze({
  mudur: 'Müdür',
  mudur_yardimcisi: 'Müdür Yardımcısı',
  bolge_muhendisi: 'Bölge Mühendisi',
  formen: 'Formen',
  park_gorevlisi: 'Park Görevlisi',
});

export function roleLabel(slug) {
  return OBT_ROLE_LABELS[slug] || String(slug || '');
}

/** Backend alan sınırları (schemas.py) — istemci doğrulaması için. */
export const OBT_LIMITS = Object.freeze({
  TITLE: 200,
  DESCRIPTION: 8000,
  TOPIC: 64,
  QUESTION_TEXT: 4000,
  OPTION_VALUE: 128,
  OPTION_LABEL: 500,
  MAX_TARGETS: 500,
});
